const BASE = '/backend';
const TOKEN_KEY = 'aegent_token';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_e) { return null; }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (_e) {}
}

function authHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request(path, { method = 'GET', body, query } = {}) {
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, v);
    });
  }
  const init = { method, headers: authHeaders() };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(url.pathname + url.search, init);
  if (!res.ok) {
    let detail = '';
    let data = null;
    try { data = await res.json(); detail = data?.error || ''; } catch (_e) {}
    const err = new Error(`API ${method} ${path} failed: ${res.status} ${detail}`);
    err.status = res.status;
    err.detail = detail;
    err.data = data;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

export const api = {
  get: (path, query) => request(path, { query }),
  post: (path, body, query) => request(path, { method: 'POST', body, query }),
  put: (path, body, query) => request(path, { method: 'PUT', body, query }),
  patch: (path, body, query) => request(path, { method: 'PATCH', body, query }),
  del: (path, query) => request(path, { method: 'DELETE', query }),
};

// Download a binary response and trigger a browser save. Returns the filename
// the user got (Content-Disposition wins, falls back to the suggested name).
export async function downloadFile(path, suggestedName) {
  const url = new URL(BASE + path, window.location.origin);
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url.pathname + url.search, { headers });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (_e) {}
    throw new Error(`Download failed: ${res.status} ${detail}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = (m && decodeURIComponent(m[1])) || suggestedName || 'download';
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  return name;
}

// Multipart upload of one or more files. ``files`` is a File[] (typically from
// an <input type="file" webkitdirectory>); each file's relative path inside the
// chosen folder is sent in a parallel ``paths`` array so the backend can keep
// the directory layout.
export async function uploadFolder(path, files, { stripTop = true } = {}) {
  const url = new URL(BASE + path, window.location.origin);
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const fd = new FormData();
  const paths = [];
  for (const f of files) {
    fd.append('files', f, f.name);
    paths.push(f.webkitRelativePath || f.name);
  }
  fd.append('paths', JSON.stringify(paths));
  if (stripTop) fd.append('strip_top', 'true');
  const res = await fetch(url.pathname + url.search, { method: 'POST', headers, body: fd });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (_e) {}
    throw new Error(`Upload failed: ${res.status} ${detail}`);
  }
  return res.json();
}

// ----- Auth --------------------------------------------------------------

export async function signupApi({ email, username, password }) {
  return request('/auth/signup', { method: 'POST', body: { email, username, password } });
}

export async function signinApi({ login, password }) {
  return request('/auth/signin', { method: 'POST', body: { login, password } });
}

export async function fetchMe() {
  return request('/auth/me');
}

export async function verifyEmailApi(token) {
  return request('/auth/verify-email', { method: 'POST', body: { token } });
}

export async function resendVerificationApi(email) {
  return request('/auth/resend-verification', { method: 'POST', body: { email } });
}

export async function listAgents(userId) {
  const data = await api.get('/agents', userId ? { user_id: userId } : undefined);
  return data?.agents || [];
}

export async function fetchAgent(agentId) {
  return api.get(`/agents/${agentId}`);
}

export async function createAgentApi(userId, agentData) {
  const payload = { ...agentData, user_id: userId };
  const data = await api.post('/agents', payload);
  return data?.id;
}

export async function patchAgent(agentId, updates) {
  return api.patch(`/agents/${agentId}`, updates);
}

export async function deleteAgentApi(agentId) {
  return api.del(`/agents/${agentId}`);
}

export async function fetchKnowledgeBase(agentId) {
  const data = await api.get(`/agents/${agentId}/knowledge-base`);
  return data?.entries || [];
}

export async function deleteKnowledgeBaseEntryApi(agentId, entryId) {
  return api.del(`/agents/${agentId}/knowledge-base/${entryId}`);
}

export async function fetchMasterMemory(agentId, limit = 200) {
  const data = await api.get(`/agents/${agentId}/master-memory`, { limit });
  return (data?.entries || []).map(e => ({ _id: e.id || e._id, ...e }));
}

export async function deleteMasterMemoryEntryApi(agentId, entryId) {
  return api.del(`/agents/${agentId}/master-memory/${entryId}`);
}

export async function clearMasterMemoryApi(agentId) {
  return api.del(`/agents/${agentId}/master-memory`);
}

export async function fetchThreadMemoryMd(agentId, threadId) {
  return api.get(`/agents/${agentId}/threads/${encodeURIComponent(threadId)}/memory-md`);
}

export async function fetchWaitingConversations(agentId) {
  const data = await api.get(`/agents/${agentId}/waiting`);
  return (data?.entries || []).map(e => ({ _id: e.id || e._id, ...e }));
}

export async function deleteWaitingConversationApi(agentId, entryId) {
  return api.del(`/agents/${agentId}/waiting/${entryId}`);
}

export async function clearWaitingConversationsApi(agentId) {
  return api.del(`/agents/${agentId}/waiting`);
}

export async function fetchListenerMemory(listenerId, limit = 50) {
  const data = await api.get(`/listeners/${listenerId}/memory`, { limit });
  return (data?.entries || []).map(e => ({ _id: e.id || e._id, ...e }));
}

export async function clearListenerMemoryApi(listenerId) {
  return api.del(`/listeners/${listenerId}/memory`);
}

export async function fireListenerTestEvent(agentId, listenerId, event) {
  return api.post(`/agents/${agentId}/listeners/${listenerId}/test_event`, { event });
}

// ----- Agent knowledge files (uploaded folders/files) ----------------------

export async function fetchAgentFiles(agentId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/files`);
}

export async function uploadAgentFiles(agentId, fileList, { folder } = {}) {
  const fd = new FormData();
  const paths = [];
  for (const f of fileList) {
    fd.append('files', f, f.name);
    paths.push(f.webkitRelativePath || '');
  }
  fd.append('paths', JSON.stringify(paths));
  if (folder) fd.append('folder', folder);

  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}/agents/${encodeURIComponent(agentId)}/files`, {
    method: 'POST',
    headers,
    body: fd,
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (_e) {}
    throw new Error(`Upload failed: ${res.status} ${detail}`);
  }
  return res.json();
}

export async function deleteAgentFileApi(agentId, fileId) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/files/${encodeURIComponent(fileId)}`);
}

export async function deleteAgentFolderApi(agentId, folderName) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/files/folder`, { name: folderName });
}

export async function fetchAgentFileText(agentId, fileId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/files/${encodeURIComponent(fileId)}/text`);
}

export async function downloadAgentFile(agentId, fileId, filename) {
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    `${BASE}/agents/${encodeURIComponent(agentId)}/files/${encodeURIComponent(fileId)}/download`,
    { headers },
  );
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).error || ''; } catch (_e) {}
    throw new Error(`Download failed: ${res.status} ${detail}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function fetchAgentEnvironment(agentId) {
  return api.get(`/agents/${agentId}/environment`);
}

export async function fetchEnvTree(agentId, path = '') {
  return api.get(`/agents/${agentId}/environment/tree`, path ? { path } : undefined);
}

export async function fetchEnvFile(agentId, path) {
  return api.get(`/agents/${agentId}/environment/file`, { path });
}

export async function putEnvFile(agentId, path, content) {
  return api.put(`/agents/${agentId}/environment/file`, { path, content });
}

export async function deleteEnvPath(agentId, path) {
  return api.del(`/agents/${agentId}/environment/file`, { path });
}

export async function createEnvFolder(agentId, path) {
  return api.post(`/agents/${agentId}/environment/folder`, { path });
}

export async function installEnvPackage(agentId, name) {
  return api.post(`/agents/${agentId}/environment/packages`, { name });
}

export async function uninstallEnvPackage(agentId, name) {
  return api.del(`/agents/${agentId}/environment/packages/${encodeURIComponent(name)}`);
}

export async function rebuildEnvVenv(agentId) {
  return api.post(`/agents/${agentId}/environment/rebuild-venv`);
}

export async function fetchEscalationLogs(agentId) {
  const data = await api.get(`/agents/${agentId}/escalation-logs`);
  return data?.entries || data?.logs || [];
}

export async function deleteEscalationLogApi(agentId, logId) {
  return api.del(`/agents/${agentId}/escalation-logs/${logId}`);
}
